
import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import { DeviceService } from "@sureview/camera";
import { SessionResource } from "@/store/sessions/types";
import { ServerDetails } from "@/store/devices/types";
import { Action, namespace } from "vuex-class";
import api from "@/services/api.service";
import { debounce } from "lodash";
import ObjectTypeId from "@/types/sv-data/enums/ObjectTypeIds";
import HealthCheckRequest from "@/types/system-health/HealthCheckRequest";

const Devices = namespace("devices");
const Session = namespace("sessions")

@Component({})
export default class PingButton extends Vue {

	@Session.Action updateSession: any;
	@Session.Getter getSession: any;

	@Prop(Number) private objectId: number;
	@Prop(Number) private objectTypeId: number;

	@Action("raiseSystemLog") private raiseSystemLog: (healthcheckRequest: HealthCheckRequest) => Promise<void>;

	@Devices.Action private getServer: (serverId: number) => Promise<void>;
	@Devices.State("serversList") private serversListFromStore: ServerDetails[];

	private serverId: number | null = null;
	private isServer: boolean;
	private pingResult: boolean = null;
	private debouncePing;
	private isLoading: boolean = true;

	private tooltip: string = "";

	public async mounted(): Promise<void> {
		this.updateSession({
			resourceId: SessionResource.DeviceServiceSession
		});

		this.debouncePing = debounce(() => {
			this.performPing();
		}, 500);

		await this.reset();
		this.isLoading = false;
	}

	@Watch("objectId", { immediate: true })
	public async reset(): Promise<void> {
		this.isLoading = true;
		this.pingResult = null;
		this.tooltip = "Ping this device, and update it's current status";
		this.isServer = this.objectTypeId == ObjectTypeId.Server;

		if (this.isServer) {
			this.serverId = this.objectId;
		}
		else {
			await this.fetchServerId();
		}
		this.isLoading = false;
	}

	private async performPing(): Promise<void> {
		if (!this.serverId) {
			this.$notify({
				type: "error",
				title: "Device Ping",
				text: "This device has no server ID set"
			});
			return;
		}

		let auth = this.getSession(SessionResource.DeviceServiceSession);
		let deviceService = new DeviceService(api.getDeviceServiceAddress());

		try {
			let response = await deviceService.pingDeviceWithServerId(auth, this.serverId);
			this.pingResult = true;

			this.$notify({
				type: "success",
				title: "Device Ping",
				text: "The ping was successful"
			});

			this.tooltip = "The last ping attempt was successful";

			await this.raiseSystemLog({
				ObjectId: this.objectId,
				ObjectTypeId: this.objectTypeId,
				IsError: false,
				Message: "Manual ping success",
				Data: response.statusText,
				EventId: null,
			});

			this.$emit("health-history-changed");
		}
		catch(error) {
			this.pingResult = false;
			this.$notify({
				type: "error",
				title: "Device Ping",
				text: `The ping was not successful. ${error.statusText}`
			});
			this.tooltip = error.statusText;

			await this.raiseSystemLog({
				ObjectId: this.objectId,
				ObjectTypeId: this.objectTypeId,
				IsError: true,
				Message: `Manual ping failed. ${error.statusText}`,
				Data: error.statusText,
				EventId: null,
			});

			this.$emit("health-history-changed");
		}
	}

	private async fetchServerId(): Promise<void> {
		try {
			let deviceInfos = await api.getDeviceServerDetails([this.objectId]);
			if (deviceInfos.length !== 0) {
				this.serverId = deviceInfos[0].serverId;
			}
		}
		catch(e) {
			console.error("Failed to retrieve device server id: " + e);
		}
	}
	private get isButtonDisabled(): boolean {
		return !this.serverId;
	}

	private get buttonVariant(): string {
		if (this.pingResult !== null) {
			return this.pingResult ? "success" : "danger";
		}
		return "primary";
	}
}
